import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  makeStyles,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Collapse,
  TextField,
  Button,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  InputAdornment,
  Divider,
} from '@material-ui/core';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Check as CheckIcon,
} from '@material-ui/icons';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CatalogReviewer, CatalogReviewerUser, CatalogReviewerTeam, RequestedReviewer } from '../../types';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  content: {
    maxHeight: 400,
    overflow: 'auto',
    padding: theme.spacing(1.5),
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.grey[50],
    borderTop: `1px solid ${theme.palette.divider}`,
    gap: theme.spacing(1),
  },
  closeButton: {
    padding: theme.spacing(0.5),
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  error: {
    padding: theme.spacing(2),
    color: theme.palette.error.main,
  },
  section: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  searchField: {
    marginBottom: theme.spacing(1),
  },
  resultsList: {
    maxHeight: 200,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  listItem: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  selectedChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  userChip: {
    backgroundColor: theme.palette.info.light,
  },
  teamChip: {
    backgroundColor: theme.palette.secondary.light,
  },
  noResults: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  typeIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
  },
  submitButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    '&:disabled': {
      backgroundColor: theme.palette.grey[300],
    },
  },
  currentReviewersSection: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.success.light + '15',
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.success.light}`,
  },
  currentReviewerChip: {
    backgroundColor: theme.palette.success.light,
    marginRight: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
  },
  currentReviewerIcon: {
    color: theme.palette.success.main,
  },
  alreadyRequestedIcon: {
    color: theme.palette.success.main,
  },
}));

interface ReviewerSelectorProps {
  open: boolean;
  onClose: () => void;
  onRequestReviewers: (
    reviewers: { users: string[]; teams: string[] },
  ) => Promise<void>;
  submitting: boolean;
  repoOwner: string; // GitHub org for team requests
  currentReviewers?: RequestedReviewer[]; // Currently requested reviewers
}

export const ReviewerSelector = ({
  open,
  onClose,
  onRequestReviewers,
  submitting,
  repoOwner,
  currentReviewers = [],
}: ReviewerSelectorProps) => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReviewers, setSelectedReviewers] = useState<CatalogReviewer[]>([]);
  const [availableUsers, setAvailableUsers] = useState<CatalogReviewerUser[]>([]);
  const [availableTeams, setAvailableTeams] = useState<CatalogReviewerTeam[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch users and teams from the catalog
  const fetchReviewers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch users with github.com/user-login annotation
      const usersResponse = await catalogApi.getEntities({
        filter: { kind: 'User' },
        fields: [
          'metadata.name',
          'metadata.annotations',
          'spec.profile.displayName',
        ],
      });

      const users: CatalogReviewerUser[] = usersResponse.items
        .filter(entity => {
          const githubLogin = entity.metadata.annotations?.['github.com/user-login'];
          return !!githubLogin;
        })
        .map(entity => ({
          type: 'user' as const,
          entityRef: `user:default/${entity.metadata.name}`,
          displayName:
            (entity.spec as any)?.profile?.displayName ||
            entity.metadata.name,
          githubLogin: entity.metadata.annotations!['github.com/user-login'],
        }));

      // Fetch groups with github.com/team-slug annotation
      const groupsResponse = await catalogApi.getEntities({
        filter: { kind: 'Group' },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.annotations',
          'spec.profile.displayName',
        ],
      });

      const teams: CatalogReviewerTeam[] = groupsResponse.items
        .filter(entity => {
          const teamSlug = entity.metadata.annotations?.['github.com/team-slug'];
          return !!teamSlug;
        })
        .map(entity => ({
          type: 'team' as const,
          entityRef: `group:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`,
          displayName:
            (entity.spec as any)?.profile?.displayName ||
            entity.metadata.name,
          githubTeamSlug: entity.metadata.annotations!['github.com/team-slug'],
        }));

      setAvailableUsers(users);
      setAvailableTeams(teams);
    } catch (err) {
      console.error('Failed to fetch reviewers from catalog:', err);
      setError('Failed to load reviewers from catalog');
    } finally {
      setLoading(false);
    }
  }, [catalogApi]);

  useEffect(() => {
    if (open) {
      fetchReviewers();
    }
  }, [open, fetchReviewers]);

  // Filter results based on search query
  const filteredResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) {
      return { users: availableUsers.slice(0, 10), teams: availableTeams.slice(0, 10) };
    }

    const filteredUsers = availableUsers.filter(
      user =>
        user.displayName.toLowerCase().includes(query) ||
        user.githubLogin.toLowerCase().includes(query)
    );

    const filteredTeams = availableTeams.filter(
      team =>
        team.displayName.toLowerCase().includes(query) ||
        team.githubTeamSlug.toLowerCase().includes(query)
    );

    return { users: filteredUsers.slice(0, 10), teams: filteredTeams.slice(0, 10) };
  }, [searchQuery, availableUsers, availableTeams]);

  const handleAddReviewer = (reviewer: CatalogReviewer) => {
    if (!selectedReviewers.some(r => r.entityRef === reviewer.entityRef)) {
      setSelectedReviewers([...selectedReviewers, reviewer]);
    }
  };

  const handleRemoveReviewer = (entityRef: string) => {
    setSelectedReviewers(selectedReviewers.filter(r => r.entityRef !== entityRef));
  };

  const handleSubmit = async () => {
    const users = selectedReviewers
      .filter((r): r is CatalogReviewerUser => r.type === 'user')
      .map(r => r.githubLogin);

    const teams = selectedReviewers
      .filter((r): r is CatalogReviewerTeam => r.type === 'team')
      .map(r => r.githubTeamSlug);

    await onRequestReviewers({ users, teams });

    // Reset state after successful submission
    setSelectedReviewers([]);
    setSearchQuery('');
  };

  const handleCancel = () => {
    setSelectedReviewers([]);
    setSearchQuery('');
    onClose();
  };

  const isAlreadySelected = (reviewer: CatalogReviewer) =>
    selectedReviewers.some(r => r.entityRef === reviewer.entityRef);

  // Check if a reviewer is already requested on the PR
  const isAlreadyRequested = (reviewer: CatalogReviewer): boolean => {
    if (reviewer.type === 'user') {
      return currentReviewers.some(
        r => r.type === 'user' && r.login.toLowerCase() === reviewer.githubLogin.toLowerCase()
      );
    }
    if (reviewer.type === 'team') {
      return currentReviewers.some(
        r => r.type === 'team' && r.login.toLowerCase() === reviewer.githubTeamSlug.toLowerCase()
      );
    }
    return false;
  };

  const canSubmit = selectedReviewers.length > 0 && !submitting;

  return (
    <Collapse in={open}>
      <Paper className={classes.container} elevation={0}>
        <Box className={classes.header}>
          <Typography variant="subtitle2">Request Review</Typography>
          <IconButton
            size="small"
            onClick={onClose}
            className={classes.closeButton}
            aria-label="Close reviewer selector"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box className={classes.content}>
          {loading && (
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" style={{ marginLeft: 8 }}>
                Loading reviewers...
              </Typography>
            </Box>
          )}

          {error && (
            <Box className={classes.error}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}

          {!loading && !error && (
            <>
              {/* Currently Requested Reviewers */}
              {currentReviewers.length > 0 && (
                <Box className={classes.currentReviewersSection}>
                  <Typography className={classes.sectionTitle}>
                    <CheckIcon className={classes.currentReviewerIcon} />
                    Currently Requested
                  </Typography>
                  <Box className={classes.selectedChips}>
                    {currentReviewers.map(reviewer => (
                      <Chip
                        key={`${reviewer.type}-${reviewer.login}`}
                        icon={reviewer.type === 'user' ? <PersonIcon /> : <GroupIcon />}
                        label={reviewer.displayName || reviewer.login}
                        size="small"
                        className={classes.currentReviewerChip}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Selected Reviewers */}
              {selectedReviewers.length > 0 && (
                <Box className={classes.section}>
                  <Typography className={classes.sectionTitle}>
                    Selected Reviewers
                  </Typography>
                  <Box className={classes.selectedChips}>
                    {selectedReviewers.map(reviewer => (
                      <Chip
                        key={reviewer.entityRef}
                        icon={reviewer.type === 'user' ? <PersonIcon /> : <GroupIcon />}
                        label={reviewer.displayName}
                        size="small"
                        className={
                          reviewer.type === 'user' ? classes.userChip : classes.teamChip
                        }
                        onDelete={() => handleRemoveReviewer(reviewer.entityRef)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Search Field */}
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>
                  <PersonIcon className={classes.typeIcon} />
                  Add Users or Teams
                </Typography>
                <TextField
                  placeholder="Search by name or GitHub username..."
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className={classes.searchField}
                  disabled={submitting}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Results List */}
                <Box className={classes.resultsList}>
                  {filteredResults.users.length === 0 &&
                    filteredResults.teams.length === 0 && (
                      <Box className={classes.noResults}>
                        <Typography variant="body2">
                          {searchQuery
                            ? 'No matching users or teams found'
                            : 'Start typing to search for users or teams'}
                        </Typography>
                      </Box>
                    )}

                  {/* Users */}
                  {filteredResults.users.length > 0 && (
                    <>
                      <List dense disablePadding>
                        {filteredResults.users.map(user => {
                          const alreadyRequested = isAlreadyRequested(user);
                          const alreadySelected = isAlreadySelected(user);
                          return (
                            <ListItem
                              key={user.entityRef}
                              className={classes.listItem}
                              disabled={alreadySelected || alreadyRequested}
                            >
                              <ListItemAvatar>
                                <Avatar>
                                  <PersonIcon />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={user.displayName}
                                secondary={alreadyRequested ? `@${user.githubLogin} (already requested)` : `@${user.githubLogin}`}
                              />
                              <ListItemSecondaryAction>
                                {alreadyRequested ? (
                                  <CheckIcon className={classes.alreadyRequestedIcon} />
                                ) : (
                                  <IconButton
                                    edge="end"
                                    size="small"
                                    onClick={() => handleAddReviewer(user)}
                                    disabled={alreadySelected || submitting}
                                  >
                                    <AddIcon />
                                  </IconButton>
                                )}
                              </ListItemSecondaryAction>
                            </ListItem>
                          );
                        })}
                      </List>
                      {filteredResults.teams.length > 0 && <Divider />}
                    </>
                  )}

                  {/* Teams */}
                  {filteredResults.teams.length > 0 && (
                    <List dense disablePadding>
                      {filteredResults.teams.map(team => {
                        const alreadyRequested = isAlreadyRequested(team);
                        const alreadySelected = isAlreadySelected(team);
                        return (
                          <ListItem
                            key={team.entityRef}
                            className={classes.listItem}
                            disabled={alreadySelected || alreadyRequested}
                          >
                            <ListItemAvatar>
                              <Avatar>
                                <GroupIcon />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={team.displayName}
                              secondary={alreadyRequested ? `@${repoOwner}/${team.githubTeamSlug} (already requested)` : `@${repoOwner}/${team.githubTeamSlug}`}
                            />
                            <ListItemSecondaryAction>
                              {alreadyRequested ? (
                                <CheckIcon className={classes.alreadyRequestedIcon} />
                              ) : (
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleAddReviewer(team)}
                                  disabled={alreadySelected || submitting}
                                >
                                  <AddIcon />
                                </IconButton>
                              )}
                            </ListItemSecondaryAction>
                          </ListItem>
                        );
                      })}
                    </List>
                  )}
                </Box>
              </Box>
            </>
          )}
        </Box>

        <Box className={classes.footer}>
          <Button size="small" onClick={handleCancel} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            className={classes.submitButton}
            onClick={handleSubmit}
            disabled={!canSubmit}
            startIcon={
              submitting ? <CircularProgress size={14} color="inherit" /> : undefined
            }
          >
            {submitting ? 'Requesting...' : 'Request Review'}
          </Button>
        </Box>
      </Paper>
    </Collapse>
  );
};
